import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useQueryClient } from '@zg-rentals/react-query5';
import { TOUR_CALENDAR_MODES, useAvailabilityDates, useTourCalendarApi } from '@zg-rentals/touring';

import type React from 'react';
import type { AvailabilityDate, AvailabilityEvent, TimeZoneIds } from '@zg-rentals/touring';
import type { TourDuration } from '@zg-rentals/ts-z-calendar-client';
import { type TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';

export interface TourCalendarApiType {
  availabilityEvents: Array<AvailabilityEvent>;
  tourConfirmationType?: TourConfirmationType;
  timeZone?: TimeZoneIds;
  tourDuration?: TourDuration;
  enableTouringForListing: (
    method: TourConfirmationType,
    zone: TimeZoneIds,
    duration?: TourDuration,
  ) => Promise<unknown>;
  setAvailabilitiesLite: (availabilityDto: unknown) => Promise<unknown>;
  isEnableTouringForListingLoading: boolean;
  isGetTourConfigAndAvailabilityLoading: boolean;
  [key: string]: unknown;
}

export interface TourSchedulingContextType {
  selectedBookingMethod?: TourConfirmationType;
  selectedTimeZone?: TimeZoneIds;
  selectedTourDuration?: TourDuration;
  availabilityDates: Array<AvailabilityDate>;
  canAccessPostingPathTourScheduling: boolean;

  setSelectedBookingMethod: (method: TourConfirmationType | undefined) => void;
  setSelectedTimeZone: (zone: TimeZoneIds | undefined) => void;
  setSelectedTourDuration: (duration: TourDuration | undefined) => void;
  setAvailabilityDates: (dates: Array<AvailabilityDate>) => void;

  tourCalendarApiHook: ReturnType<typeof useTourCalendarApi>;
}

export const TourSchedulingContext = createContext<TourSchedulingContextType | undefined>(undefined);

interface TourSchedulingProviderProps {
  children: React.ReactNode;
  listingAlias: string;
  initialState?: {
    selectedTimeZone?: TimeZoneIds;
    tourDuration?: TourDuration;
    availabilityDates?: Array<AvailabilityDate>;
  };
  canAccessPostingPathTourScheduling: boolean;
  selectedBookingMethod: TourConfirmationType | undefined;
  setSelectedBookingMethod: (method: TourConfirmationType | undefined) => void;
}

export const useTourSchedulingContext = (): TourSchedulingContextType => {
  const context = useContext(TourSchedulingContext);

  if (context === undefined) {
    throw new Error('useTourSchedulingContext must be used within a TourSchedulingProvider');
  }

  return context;
};

export const TourSchedulingProvider: React.FC<TourSchedulingProviderProps> = ({
  listingAlias,
  children,
  initialState,
  canAccessPostingPathTourScheduling,
  selectedBookingMethod,
  setSelectedBookingMethod,
}) => {
  const queryClient = useQueryClient();
  const [selectedTimeZone, setSelectedTimeZone] = useState<TimeZoneIds | undefined>(initialState?.selectedTimeZone);
  const [selectedTourDuration, setSelectedTourDuration] = useState<TourDuration | undefined>(
    initialState?.tourDuration,
  );
  const [availabilityDates, setAvailabilityDates] = useState<Array<AvailabilityDate>>(
    initialState?.availabilityDates || [],
  );

  const onTouringToggleSuccess = useCallback(
    (isDisabling?: boolean) => {
      if (isDisabling) {
        setSelectedBookingMethod(undefined);
        setSelectedTimeZone(undefined);
      }
      queryClient.invalidateQueries({
        predicate: ({ meta }) => meta?.tags?.includes('listingRecommendations') ?? false,
      });
    },
    [queryClient, setSelectedBookingMethod, setSelectedTimeZone],
  );

  const tourCalendarApiHook = useTourCalendarApi({
    enableQueries: Boolean(canAccessPostingPathTourScheduling && listingAlias),
    listingAlias,
    mode: TOUR_CALENDAR_MODES.TOUR_AVAILABILITY,
    onTouringToggleSuccess,
  });

  const [currentAvailabilityDates] = useAvailabilityDates(tourCalendarApiHook.availabilityEvents);

  useEffect(() => {
    setSelectedBookingMethod(tourCalendarApiHook.tourConfirmationType);
  }, [setSelectedBookingMethod, tourCalendarApiHook.tourConfirmationType]);

  useEffect(() => {
    setSelectedTimeZone(tourCalendarApiHook.timeZone);
  }, [setSelectedTimeZone, tourCalendarApiHook.timeZone]);

  useEffect(() => {
    setSelectedTourDuration(tourCalendarApiHook.tourDuration);
  }, [setSelectedTourDuration, tourCalendarApiHook.tourDuration]);

  useEffect(() => {
    setAvailabilityDates(currentAvailabilityDates);
  }, [currentAvailabilityDates, setAvailabilityDates]);

  const contextValue: TourSchedulingContextType = {
    // Shared state
    selectedBookingMethod,
    selectedTimeZone,
    selectedTourDuration,
    availabilityDates,
    canAccessPostingPathTourScheduling,

    // State setters
    setSelectedBookingMethod,
    setSelectedTimeZone,
    setSelectedTourDuration,
    setAvailabilityDates,

    // Tour Calendar API object
    tourCalendarApiHook,
  };

  return <TourSchedulingContext.Provider value={contextValue}>{children}</TourSchedulingContext.Provider>;
};
